import {
    ErrorResponse,
    NetworkParams,
    RestService,
} from "@sinossi/mates-react-library"

import {
    Asset,
    AssetFilter,
    AssetWithImage,
    CreateAssetRequest,
    CreateAssetResponse,
    Document,
    DocumentType,
    ListLandlordsProjection,
    MergeAssetRequest,
    NewAssetFilter,
    PaginatedResponse,
    SearchFilter,
    UpdateAssetRequest
} from "models"

export class AssetService {
    static filterAssets = (request: AssetFilter | null, callback: any, catchCallback: any) => {
        RestService
            .post<PaginatedResponse<AssetWithImage>>("/api/assets/filter", request)
            .then(callback)
            .catch(catchCallback)
    }

    static getAssetsByArea = (request: NewAssetFilter, callback: any, catchCallback: any) => {
        RestService
            .post<PaginatedResponse<AssetWithImage>>("/api/assets/by-area", request)
            .then(callback)
            .catch(catchCallback)
    }

    static getLikedAssets = (callback: any, catchCallback: any) => {
        RestService
            .post<PaginatedResponse<AssetWithImage>>("/api/liked-assets", {})
            .then(callback)
            .catch(catchCallback)
    }

    static getLandlord = (callback: any, catchCallback: any) => {
        RestService
            .post<PaginatedResponse<ListLandlordsProjection>>("/api/landlord", null)
            .then(callback)
            .catch(catchCallback)
    }

    static getAsset = (assetId: string, callback: any, catchCallback: any) => {
        RestService
            .get<Asset>(`/api/asset/${assetId}`)
            .then(callback)
            .catch(catchCallback)
    }

    static updateAsset = (assetId: string, request: UpdateAssetRequest, callback: any, catchCallback: any) => {
        RestService
            .put(`/api/assets/${assetId}`, request)
            .then(callback)
            .catch(catchCallback)
    }

    static createAsset = (body: CreateAssetRequest, callback: any, catchCallback: any) => {
        RestService
            .post<CreateAssetResponse>(`/api/asset`, body)
            .then(callback)
            .catch(catchCallback)
    }

    static uploadDocument = (assetId: string, request: FormData, callback: any, catchCallback: any) => {
        const params = new NetworkParams()
        params.contentType = undefined

        RestService
            .genericPost<any, ErrorResponse>(`/api/asset/${assetId}/document`, request, params)
            .then(callback)
            .catch(catchCallback)
    }

    static uploadImmobiliareBaseData = (request: FormData, callback: any, catchCallback: any) => {
        const params = new NetworkParams()
        params.contentType = undefined

        RestService
            .genericPost<any, ErrorResponse>(`/api/assets/immobiliare-base`, request, params)
            .then(callback)
            .catch(catchCallback)
    }

    static uploadImmobiliareAdvancedData = (request: FormData, callback: any, catchCallback: any) => {
        const params = new NetworkParams()
        params.contentType = undefined

        RestService
            .genericPost<any, ErrorResponse>(`/api/assets/immobiliare-additional-info`, request, params)
            .then(callback)
            .catch(catchCallback)
    }

    static uploadStorebookCsv = (request: FormData, callback: any, catchCallback: any) => {
        const params = new NetworkParams()
        params.contentType = undefined

        RestService
            .genericPost<any, ErrorResponse>(`/api/assets/storebook-csv`, request, params)
            .then(callback)
            .catch(catchCallback)
    }

    static getDocuments = (assetId: string, documentType: DocumentType, param: SearchFilter, callback: any, catchCallback: any, onlyPublic: boolean = false, brandId: any = null) => {
        let searchParams: URLSearchParams = new URLSearchParams()
        searchParams.append("pageSize", "" + param.pageSize)
        searchParams.append("pageId", "" + param.pageId)
        searchParams.append("documentType", documentType)

        if (param.orderType && param.orderBy) {
            searchParams.append("orderType", param.orderType!.toString())
            searchParams.append("orderBy", param.orderBy!.toString())
        } else {
            searchParams.append("orderType", "ASCENDING")
            searchParams.append("orderBy", "name")
        }

        if (brandId != null)
            searchParams.append("brandId", brandId)

        searchParams.append("onlyPublic", onlyPublic ? "true" : "false")

        RestService
            .get<PaginatedResponse<Document>>(`/api/asset/${assetId}/document`, NetworkParams.EMPTY, searchParams)
            .then(callback)
            .catch(catchCallback)
    }

    static getPhotos = (assetId: string, documentType: DocumentType, param: SearchFilter, callback: any, catchCallback: any) => {
        let searchParams: URLSearchParams = new URLSearchParams()
        searchParams.append("pageSize", "" + param.pageSize)
        searchParams.append("pageId", "" + param.pageId)
        searchParams.append("documentType", documentType)

        if (param.orderType && param.orderBy) {
            searchParams.append("orderType", param.orderType!.toString())
            searchParams.append("orderBy", param.orderBy!.toString())
        } else {
            searchParams.append("orderType", "ASCENDING")
            searchParams.append("orderBy", "name")
        }

        RestService
            .get<PaginatedResponse<Document>>(`/api/asset/${assetId}/document`, NetworkParams.EMPTY, searchParams)
            .then(callback)
            .catch(catchCallback)
    }

    static deleteDocument = (assetId: string, documentId: string, callback: any, catchCallback: any) => {
        RestService
            .delete(`/api/asset/${assetId}/document/${documentId}`)
            .then(callback)
            .catch(catchCallback)
    }

    static updateGmaps = (assetId: string, callback: any, catchCallback: any) => {
        RestService
            .post(`/api/assets/${assetId}/gmaps-analyse`, assetId)
            .then(callback)
            .catch(catchCallback)
    }

    static updateImmobiliare = (assetId: string, callback: any, catchCallback: any) => {
        RestService
            .post(`/api/assets/${assetId}/immobiliareit-analyse`, assetId)
            .then(callback)
            .catch(catchCallback)
    }

    static startAiAnalysis = (assetId: string, callback: any, catchCallback: any) => {
        RestService
            .post(`/api/ai/${assetId}/start-analysis`, assetId)
            .then(callback)
            .catch(catchCallback)
    }

    static addToFavourite = (assetId: string, callback: any, catchCallback: any) => {
        RestService
            .post(`/api/asset/${assetId}/like`, assetId)
            .then(callback)
            .catch(catchCallback)
    }

    static removeFromFavourite = (assetId: string, callback: any, catchCallback: any) => {
        RestService
            .delete(`/api/asset/${assetId}/like`)
            .then(callback)
            .catch(catchCallback)
    }

    static getLandlordData = (landlordId: string, callback: any, catchCallback: any) => {
        RestService
            .get(`/api/landlord/${landlordId}`)
            .then(callback)
            .catch(catchCallback)
    }

    static mergeAssets = (request: MergeAssetRequest, callback: any, catchCallback: any) => {
        RestService
            .post(`/api/assets/merge`, request)
            .then(callback)
            .catch(catchCallback)
    }
}
